import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ComponentType, type CSSProperties } from "react";
// The same drag/resize grid the Director dashboard uses (ADR 0124): the classic
// cols/rowHeight/WidthProvider API at the React-19-safe `/legacy` entry. The
// native ResizeObserver it needs is polyfilled for jsdom in setupTests.
import ReactGridLayout, { WidthProvider, type Layout, type LegacyReactGridLayoutProps } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { brandedQrDataUrl } from "./brandedQr";
import { Button, Chip, Panel } from "@legends/ui";
import { openLobbyPreviewChannel, type LobbyPreviewBundle, type LobbyPreviewChannel } from "./lobbyPreviewBus";
import { AsyncState, formatPrice, monoLabelStyle, type LoadStatus } from "./sections";
import { KIND_EYEBROW } from "./lobbyLabels";
import {
  ApiError,
  createLobbyPreviewSession,
  deleteLobbyAsset,
  fetchCaveLegend,
  fetchCaveTicketTypes,
  fetchLobbyAssets,
  fetchLobbyEditor,
  fetchLobbyRevisions,
  fetchPublicCaveEvents,
  publishLobby,
  pushLobbyPreviewSnapshot,
  restoreLobbyRevision,
  saveLobbyDraft,
  uploadLobbyAsset,
  type CaveEvent,
  type Legend,
  type LobbyAsset,
  type LobbyAssetRow,
  type LobbyDocument,
  type LobbyModule,
  type LobbyModuleKind,
  type LobbyRevisionRow,
  type LobbySpan,
  type TicketType,
} from "./api";

// The Cave Lobby editor (ADR 0131, L6) — a cave-admin surface (distinct from the
// Director live workspace) that authors the versioned lobby document: add/edit/
// reorder the approved modules, choose widths, pick the ONE featured offer (price
// read-only — content editing never grants pricing authority), upload images,
// preview desktop/phone as guest or ticket-holder, then Save draft / Publish /
// Restore. The server owns validation + authority (publish needs admin); this
// surfaces its field-specific errors rather than duplicating the rules.

const KIND_LABEL: Record<LobbyModuleKind, string> = {
  story: "Story",
  linkedCards: "Linked cards",
  stats: "Stats",
  programming: "Programming",
  partners: "Partners",
  social: "Social",
};

// Widths each kind may use on the 6-column grid — mirrors lobby.allowedSpans (Go).
const ALLOWED_SPANS: Record<LobbyModuleKind, LobbySpan[]> = {
  story: [6, 4, 3],
  linkedCards: [6, 3],
  stats: [6, 2],
  programming: [6, 4],
  partners: [6, 3],
  social: [6],
};
const SPAN_LABEL: Record<number, string> = { 6: "Full", 4: "Two-thirds", 3: "Half", 2: "Third" };

// The lobby module canvas is a 6-column drag/resize grid (like the Director's).
const Grid = WidthProvider(ReactGridLayout) as ComponentType<Omit<LegacyReactGridLayoutProps, "width">>;
const CARD_ROWS = 5; // fixed card height in grid rows (only WIDTH is resizable)

/** Snap a resized width (1..6 cols) to the nearest span the kind actually allows. */
function snapSpan(kind: LobbyModuleKind, w: number): LobbySpan {
  const allowed = ALLOWED_SPANS[kind];
  return allowed.reduce((best, s) => (Math.abs(s - w) < Math.abs(best - w) ? s : best), allowed[0]);
}

/** One grid item (a subset of RGL's Layout item, the fields we set/read). */
export interface LobbyLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  maxW?: number;
}

/** Pack the ordered modules into a row-filling 6-col layout (matches the render
 *  flow: fill left-to-right, wrap, never backfill out of order). */
export function deriveLobbyLayout(modules: LobbyModule[]): LobbyLayoutItem[] {
  let x = 0;
  let y = 0;
  const out: LobbyLayoutItem[] = [];
  for (const m of modules) {
    const w = m.span;
    if (x + w > 6) {
      x = 0;
      y += CARD_ROWS;
    }
    const allowed = ALLOWED_SPANS[m.kind];
    out.push({ i: m.id, x, y, w, h: CARD_ROWS, minW: Math.min(...allowed), maxW: Math.max(...allowed) });
    x += w;
  }
  return out;
}

/** Read a (possibly dragged/resized) grid layout back into an ordered module list:
 *  order by row then column, span = the snapped resized width. Pure — unit-tested. */
export function applyLobbyLayout(layout: LobbyLayoutItem[], modules: LobbyModule[]): LobbyModule[] {
  const byId = new Map(modules.map((m) => [m.id, m]));
  const ordered = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);
  const next: LobbyModule[] = [];
  const placed = new Set<string>();
  for (const it of ordered) {
    const m = byId.get(String(it.i));
    if (!m || placed.has(m.id)) continue;
    placed.add(m.id);
    const span = snapSpan(m.kind, it.w);
    next.push(span === m.span ? m : { ...m, span });
  }
  // Safety: keep any module the layout didn't mention, in its original order.
  for (const m of modules) if (!placed.has(m.id)) next.push(m);
  return next;
}

/** True when the two module lists have the same ids in the same order + spans. */
function sameOrderAndSpans(a: LobbyModule[], b: LobbyModule[]): boolean {
  return a.length === b.length && a.every((m, i) => b[i].id === m.id && b[i].span === m.span);
}

function defaultContent(kind: LobbyModuleKind): unknown {
  switch (kind) {
    case "story":
      return { body: "" };
    case "linkedCards":
      return { cards: [{ title: "" }] };
    case "stats":
      return { items: [{ val: "", label: "" }, { val: "", label: "" }] };
    case "programming":
      return { mode: "both", previewCount: 4 };
    case "partners":
      return { entries: [{ name: "" }] };
    case "social":
      return { links: [{ platform: "", url: "" }] };
  }
}

const label: CSSProperties = { ...monoLabelStyle, display: "block", marginBottom: 4 };
const input: CSSProperties = {
  width: "100%",
  padding: "7px 9px",
  borderRadius: "var(--radius-sm)",
  border: "1px solid var(--border-mute)",
  background: "var(--surface-quiet)",
  color: "var(--text-hi)",
  fontSize: "var(--fs-body-2)",
  boxSizing: "border-box",
};

// A registry of AssetPickers that hold a selected-but-not-yet-uploaded file
// (LSC-337). The editor blocks Publish while any is pending so a nontechnical
// operator can't publish thinking an image is attached when the upload never
// completed (choosing a file ≠ uploading — the alt-text + Upload step is required).
const PendingUploadsCtx = createContext<((id: string, pending: boolean) => void) | null>(null);

export interface CaveLobbyEditorProps {
  caveId: string;
  caveName: string;
  /** Which Cave (front) to author (ADR 0186 Phase 2); omitted = the backend's default
   *  front. Only the lobby-document calls carry it — assets/tickets/legend/events stay
   *  backend-scoped (they're shared across a backend's fronts). */
  frontId?: string;
  /** Initial publish-authority hint (e.g. the viewer is a tenant admin). The editor
   *  then trusts the server's authoritative value from the lobby load — admin OR a
   *  per-cave publish grant (LSC-262). Drafting is always allowed under cave_config. */
  canPublish: boolean;
}

export default function CaveLobbyEditor({ caveId, caveName, frontId, canPublish: canPublishInitial }: CaveLobbyEditorProps) {
  // Authoritative publish authority comes from the server (admin OR the per-cave
  // grant); the prop is only the initial hint (LSC-262).
  const [canPublish, setCanPublish] = useState(canPublishInitial);
  // Pending (selected-but-not-uploaded) image pickers (LSC-337): Publish is blocked
  // while any is unresolved so a half-finished image upload can't silently publish.
  const [pendingUploads, setPendingUploads] = useState<Set<string>>(new Set());
  const setPending = useCallback((id: string, pending: boolean) => {
    setPendingUploads((prev) => {
      if (pending === prev.has(id)) return prev;
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const hasPendingUpload = pendingUploads.size > 0;
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [doc, setDoc] = useState<LobbyDocument | null>(null);
  const [draftRev, setDraftRev] = useState(0);
  const [publishedRev, setPublishedRev] = useState(0);
  const [publishedBy, setPublishedBy] = useState<string | undefined>();
  const [publishedAt, setPublishedAt] = useState<string | undefined>();

  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [assets, setAssets] = useState<LobbyAssetRow[]>([]);
  const [legend, setLegend] = useState<Legend | null>(null);
  const [events, setEvents] = useState<CaveEvent[]>([]);

  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<LobbyRevisionRow[] | null>(null);

  // Which module the detail pane edits (drag reorders + resizes on the canvas).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Same-browser live preview windows (ADR 0134): the editor broadcasts its render
  // bundle over a per-cave BroadcastChannel; Desktop/Phone buttons window.open a
  // preview that mirrors it live.
  const previewChan = useRef<LobbyPreviewChannel | null>(null);
  const bundleRef = useRef<LobbyPreviewBundle | null>(null);
  // "Preview on my device" (ADR 0134 part B): a code-authed QR the operator scans
  // on a phone; while active, the editor pushes snapshots to the server session.
  const [device, setDevice] = useState<{ code: string; url: string; qr: string } | null>(null);
  const [deviceErr, setDeviceErr] = useState<string | null>(null);

  const mounted = useRef(true);
  const idSeq = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setStatus("loading");
    fetchLobbyEditor(caveId, frontId)
      .then((ed) => {
        if (!mounted.current) return;
        setDoc(ed.draft);
        setDraftRev(ed.draftRev);
        setPublishedRev(ed.publishedRev);
        setPublishedBy(ed.publishedBy);
        setPublishedAt(ed.publishedAt);
        setCanPublish(ed.canPublish);
        setDirty(false);
        setStatus("ready");
      })
      .catch(() => mounted.current && setStatus("error"));
  }, [caveId, frontId]);

  useEffect(() => {
    // Cave isolation (LSC-260): when the cave changes, fully reset transient
    // per-cave state so nothing — module selection, the device-preview session,
    // revision history, notices/errors — leaks from one Cave into another. (The
    // mount site also keys by cave id for a hard remount; this keeps the editor
    // self-isolating regardless of how it's mounted.)
    setSelectedId(null);
    setDevice(null);
    setDeviceErr(null);
    setRevisions(null);
    setNotice(null);
    setError(null);
    load();
    fetchCaveTicketTypes(caveId).then((t) => mounted.current && setTickets(t)).catch(() => undefined);
    fetchLobbyAssets(caveId).then((a) => mounted.current && setAssets(a)).catch(() => undefined);
    fetchCaveLegend(caveId).then((l) => mounted.current && setLegend(l)).catch(() => undefined);
    fetchPublicCaveEvents(caveId).then((e) => mounted.current && setEvents(e)).catch(() => undefined);
  }, [caveId, load]);

  // id→resolved asset map for the live preview (mirrors the public read shape).
  const assetMap = useMemo<Record<string, LobbyAsset>>(() => {
    const m: Record<string, LobbyAsset> = {};
    for (const a of assets) m[a.id] = { url: a.url, alt: a.alt, width: a.width, height: a.height };
    return m;
  }, [assets]);

  // The render bundle the preview windows consume (null until the draft loads).
  const previewBundle = useMemo<LobbyPreviewBundle | null>(
    () => (doc ? { caveId, doc, caveName, legend, assets: assetMap, tickets, events } : null),
    [caveId, doc, caveName, legend, assetMap, tickets, events],
  );

  // Open the per-cave channel once; answer a preview window's "hello" with the
  // current bundle so a freshly-opened window converges immediately.
  useEffect(() => {
    const chan = openLobbyPreviewChannel(caveId);
    previewChan.current = chan;
    const off = chan.onMessage((m) => {
      if (m.type === "hello" && bundleRef.current) chan.post(bundleRef.current);
    });
    return () => {
      off();
      chan.close();
      previewChan.current = null;
    };
  }, [caveId]);

  // Broadcast the bundle on every change (debounced), and keep a ref for "hello".
  useEffect(() => {
    bundleRef.current = previewBundle;
    if (!previewBundle) return;
    const id = window.setTimeout(() => previewChan.current?.post(previewBundle), 120);
    return () => window.clearTimeout(id);
  }, [previewBundle]);

  // Pop a live preview window (Desktop = wide, Phone = a phone-sized popup). Named
  // targets so re-clicking focuses the existing window instead of duplicating it.
  const openPreviewWindow = (mode: "desktop" | "phone") => {
    const url = `/caves/${encodeURIComponent(caveId)}/lobby/preview?mode=${mode}`;
    const features = mode === "phone" ? "width=430,height=900" : "width=1200,height=900";
    const win = window.open(url, `lobby-preview-${mode}-${caveId}`, features);
    // The window "hello"s on mount, but push once too in case it opened first.
    if (previewBundle) window.setTimeout(() => previewChan.current?.post(previewBundle), 300);
    win?.focus();
  };

  // Mint a device-preview session + QR (or close it). While open, snapshots are
  // pushed to the server session by the effect below so a scanned phone updates.
  const openDevicePreview = async () => {
    setDeviceErr(null);
    try {
      const s = await createLobbyPreviewSession(caveId);
      const qr = await brandedQrDataUrl(s.url, { size: 220 });
      setDevice({ code: s.code, url: s.url, qr });
      if (previewBundle) pushLobbyPreviewSnapshot(caveId, s.code, previewBundle).catch(() => undefined);
    } catch (e) {
      setDeviceErr(e instanceof ApiError ? e.message : "Couldn't start a device preview.");
    }
  };

  // Push the bundle to the active device session on every change (debounced).
  useEffect(() => {
    if (!device || !previewBundle) return;
    const id = window.setTimeout(() => {
      pushLobbyPreviewSnapshot(caveId, device.code, previewBundle).catch(() => undefined);
    }, 250);
    return () => window.clearTimeout(id);
  }, [device, previewBundle, caveId]);

  // ── document mutation (all immutable; each edit marks the draft dirty) ───────
  const mutate = (next: LobbyDocument) => {
    setDoc(next);
    setDirty(true);
    setNotice(null);
  };
  const setHero = (patch: Partial<LobbyDocument["hero"]>) => {
    if (!doc) return;
    mutate({ ...doc, hero: { ...doc.hero, ...patch } });
  };
  const updateModule = (id: string, patch: Partial<LobbyModule>) => {
    if (!doc) return;
    mutate({ ...doc, modules: doc.modules.map((m) => (m.id === id ? { ...m, ...patch } : m)) });
  };
  const updateContent = (id: string, contentPatch: Record<string, unknown>) => {
    if (!doc) return;
    mutate({
      ...doc,
      modules: doc.modules.map((m) =>
        m.id === id ? { ...m, content: { ...(m.content as object), ...contentPatch } } : m,
      ),
    });
  };
  const addModule = (kind: LobbyModuleKind) => {
    if (!doc) return;
    idSeq.current += 1;
    const id = `${kind}-${Date.now().toString(36)}-${idSeq.current}`;
    const m: LobbyModule = { id, kind, title: KIND_LABEL[kind], span: ALLOWED_SPANS[kind][0], content: defaultContent(kind) };
    mutate({ ...doc, modules: [...doc.modules, m] });
    setSelectedId(id);
  };
  const removeModule = (id: string) => {
    if (!doc) return;
    mutate({ ...doc, modules: doc.modules.filter((m) => m.id !== id) });
    setSelectedId((cur) => (cur === id ? null : cur));
  };
  const moveModule = (id: string, dir: -1 | 1) => {
    if (!doc) return;
    const i = doc.modules.findIndex((m) => m.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= doc.modules.length) return;
    const mods = [...doc.modules];
    [mods[i], mods[j]] = [mods[j], mods[i]];
    mutate({ ...doc, modules: mods });
  };

  // ── save / publish / restore ─────────────────────────────────────────────────
  // A validation 400 names the offending module (LSC-258): select it (gold border +
  // its detail editor opens) and scroll it into view so the operator sees exactly what
  // to fix, instead of a generic error.
  const surfaceSaveError = (e: unknown, fallback: string) => {
    if (!mounted.current) return;
    setError(e instanceof ApiError ? e.message : fallback);
    const moduleId = e instanceof ApiError ? e.moduleId : undefined;
    if (moduleId) {
      setSelectedId(moduleId);
      requestAnimationFrame(() => {
        document.querySelector(`[data-lobby-module="${CSS.escape(moduleId)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    }
  };

  const doSave = async (): Promise<number | null> => {
    if (!doc) return null;
    setBusy(true);
    setError(null);
    try {
      // Drop blank achievement pills before saving (the server rejects empties);
      // the editor keeps a blank row while you type, we just don't persist it.
      const pills = (doc.hero.achievements ?? []).map((s) => s.trim()).filter(Boolean);
      const clean = { ...doc, hero: { ...doc.hero, achievements: pills.length ? pills : undefined } };
      const res = await saveLobbyDraft(caveId, clean, draftRev, frontId);
      if (mounted.current) {
        setDraftRev(res.draftRev);
        setDirty(false);
        setNotice("Draft saved.");
      }
      return res.draftRev;
    } catch (e) {
      surfaceSaveError(e, "Couldn't save the draft.");
      return null;
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const doPublish = async () => {
    // Block publish while an image pick is mid-upload (LSC-337) — otherwise a
    // half-finished asset silently publishes as though it were attached.
    if (hasPendingUpload) {
      setError("Finish or clear the pending image upload (add alt text and click Upload) before publishing.");
      return;
    }
    // Publish the exact current draft: save first (so unsaved edits are included),
    // then publish at the resulting revision.
    const rev = dirty || draftRev === 0 ? await doSave() : draftRev;
    if (rev == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await publishLobby(caveId, rev, frontId);
      if (mounted.current) {
        setPublishedRev(res.publishedRev);
        setPublishedBy(res.publishedBy);
        setPublishedAt(new Date().toISOString());
        setNotice(`Published revision ${res.publishedRev}. It's now live.`);
      }
    } catch (e) {
      surfaceSaveError(e, "Couldn't publish.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const openRevisions = async () => {
    try {
      setRevisions(await fetchLobbyRevisions(caveId, frontId));
    } catch {
      setError("Couldn't load revision history.");
    }
  };
  const doRestore = async (rev: number) => {
    setBusy(true);
    setError(null);
    try {
      await restoreLobbyRevision(caveId, rev, draftRev, frontId);
      if (mounted.current) {
        setRevisions(null);
        setNotice(`Restored revision ${rev} into the draft. Review, then publish.`);
        load();
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof ApiError ? e.message : "Couldn't restore that revision.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  // ── asset upload / delete ──────────────────────────────────────────────────
  const onUploaded = (a: LobbyAssetRow) => setAssets((prev) => [a, ...prev]);
  const onUpload = async (file: File, alt: string): Promise<LobbyAssetRow | null> => {
    try {
      const a = await uploadLobbyAsset(caveId, file, alt);
      onUploaded(a);
      return a;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Upload failed.");
      return null;
    }
  };
  const onDeleteAsset = async (id: string) => {
    try {
      await deleteLobbyAsset(caveId, id);
      setAssets((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't delete the image.");
    }
  };

  if (status === "loading") return <AsyncState status="loading" loadingText="Loading the Designer…" />;
  if (status === "error" || !doc)
    return <AsyncState status="error" errorText="Couldn't load the Designer." onRetry={load} />;

  const featured = tickets.find((t) => t.id === doc.hero.featuredTicketTypeId) ?? null;
  const featuredMissing = !!doc.hero.featuredTicketTypeId && !featured;
  const portrait = doc.hero.portraitAssetId ? assetMap[doc.hero.portraitAssetId] : undefined;
  const background = doc.background ? assetMap[doc.background] : undefined;

  return (
    <PendingUploadsCtx.Provider value={setPending}>
    <div style={{ display: "grid", gap: 16 }} className="lobby-editor">
      {/* Live preview launchers — each opens a separate window that mirrors the
          editor as you type (same-browser BroadcastChannel; ADR 0134). */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <span style={monoLabelStyle}>Preview</span>
        <Button size="sm" variant="muted" aria-label="Open desktop preview window" onClick={() => openPreviewWindow("desktop")}>
          Desktop preview ↗
        </Button>
        <Button size="sm" variant="muted" aria-label="Open phone preview window" onClick={() => openPreviewWindow("phone")}>
          Phone preview ↗
        </Button>
        <Button size="sm" variant="muted" aria-label="Preview on my device" onClick={openDevicePreview}>
          Preview on my device ▦
        </Button>
        <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>Opens a live window that updates as you edit.</span>
      </div>

      {device && (
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            padding: 14,
            border: "1px solid var(--border-mute)",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-quiet)",
          }}
        >
          <img src={device.qr} alt="Scan to preview on your device" width={120} height={120} style={{ borderRadius: "var(--radius-sm)", background: "#fff", padding: 6 }} />
          <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
            <span style={{ ...monoLabelStyle, color: "var(--gold)" }}>Preview on my device</span>
            <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-prose)" }}>
              Scan with your phone to open a live preview — it updates as you edit (link expires in ~30 min).
            </p>
            <a href={device.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)", overflow: "hidden", textOverflow: "ellipsis" }}>
              {device.url}
            </a>
            <div>
              <Button size="sm" variant="muted" aria-label="Close device preview" onClick={() => setDevice(null)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
      {deviceErr && (
        <p role="alert" style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--alert-2)" }}>
          {deviceErr}
        </p>
      )}

      {/* ── AUTHORING CONTROLS ── */}
      <div style={{ display: "grid", gap: 14 }}>
        <Panel tier="base" title="Lobby">
          <p style={{ margin: "0 0 10px", fontSize: "var(--fs-body-2)", color: "var(--text-prose)" }}>
            {publishedRev > 0 ? (
              <>
                Published revision <strong>{publishedRev}</strong>
                {publishedBy ? ` by ${publishedBy}` : ""}
                {publishedAt ? ` · ${new Date(publishedAt).toLocaleString()}` : ""}.
              </>
            ) : (
              "Not published yet — the storefront shows the default until you publish."
            )}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <Button variant="muted" disabled={busy || !dirty} onClick={doSave} aria-label="Save draft">
              {dirty ? "Save draft" : "Saved"}
            </Button>
            {canPublish ? (
              <Button variant="accent" disabled={busy || hasPendingUpload} onClick={doPublish} aria-label="Publish lobby">
                Publish
              </Button>
            ) : (
              <span style={{ ...monoLabelStyle, color: "var(--text-dim)" }}>Publishing needs publish access for this cave</span>
            )}
            {hasPendingUpload && (
              <span role="status" style={{ ...monoLabelStyle, color: "var(--warn, var(--alert-2))" }}>
                Finish or clear the pending image upload first
              </span>
            )}
            <Button variant="muted" disabled={busy} onClick={openRevisions} aria-label="Revision history">
              History
            </Button>
          </div>
          {notice && <p style={{ margin: "10px 0 0", fontSize: "var(--fs-body-2)", color: "var(--success-hi)" }}>{notice}</p>}
          {error && (
            <p role="alert" style={{ margin: "10px 0 0", fontSize: "var(--fs-body-2)", color: "var(--alert-2)" }}>
              {error}
            </p>
          )}
        </Panel>

        {revisions && (
          <Panel tier="quiet" title="Revision history">
            {revisions.length === 0 ? (
              <p style={{ margin: 0, ...monoLabelStyle, color: "var(--text-dim)" }}>No published revisions yet.</p>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {revisions.map((r) => (
                  <div key={r.rev} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ ...monoLabelStyle, color: "var(--gold)" }}>Rev {r.rev}</span>
                    <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
                      {r.publishedBy}
                      {r.publishedAt ? ` · ${new Date(r.publishedAt).toLocaleDateString()}` : ""}
                    </span>
                    <Button size="sm" variant="muted" disabled={busy} onClick={() => doRestore(r.rev)} style={{ marginLeft: "auto" }}>
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button size="sm" variant="muted" onClick={() => setRevisions(null)} style={{ marginTop: 10 }}>
              Close
            </Button>
          </Panel>
        )}

        {/* HERO */}
        <Panel tier="quiet" title="Legend">
          <label style={label}>Featured offer</label>
          <select
            style={input}
            aria-label="Featured ticket"
            value={doc.hero.featuredTicketTypeId ?? ""}
            onChange={(e) => setHero({ featuredTicketTypeId: e.target.value || undefined })}
          >
            <option value="">— No featured offer —</option>
            {tickets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} · {formatPrice(t.priceCents)}
              </option>
            ))}
          </select>
          {featured && (
            <p style={{ margin: "6px 0 0", ...monoLabelStyle, color: "var(--text-dim)" }}>
              Price {formatPrice(featured.priceCents)} — read-only (set in Tickets)
            </p>
          )}
          {featuredMissing && (
            <p role="alert" style={{ margin: "6px 0 0", fontSize: "var(--fs-2xs)", color: "var(--alert-2)" }}>
              The selected offer no longer exists — pick a valid ticket or clear it.
            </p>
          )}
          {/* Achievement pills (LSC-294): the short badges under the legend's name,
              add/remove here. Seeded from the legend's profile, then owned by the doc. */}
          <div style={{ marginTop: 12 }}>
            <label style={label}>Achievement pills</label>
            <ListEditor<{ text: string }>
              items={(doc.hero.achievements ?? []).map((t) => ({ text: t }))}
              emptyItem={{ text: "" }}
              min={0}
              max={8}
              onChange={(items) => setHero({ achievements: items.map((x) => x.text) })}
              render={(it, set) => (
                <input
                  style={{ ...input, flex: 1 }}
                  aria-label="Achievement"
                  placeholder="e.g. 6× Super Bowl"
                  maxLength={40}
                  value={it.text}
                  onChange={(e) => set({ text: e.target.value })}
                />
              )}
            />
            <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
              Short badges under the legend's name (max 8). Blank pills are dropped on save.
            </span>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={label}>Legend portrait (3:4)</label>
            <AssetPicker
              value={doc.hero.portraitAssetId}
              resolved={portrait}
              assets={assets}
              onPick={(id) => setHero({ portraitAssetId: id })}
              onUpload={onUpload}
              onDeleteAsset={onDeleteAsset}
            />
          </div>
          {/* Cave-level background (LSC-260): a controlled backdrop image behind the
              lobby's design-system overlays — not free-form theming. Blank = default. */}
          <div style={{ marginTop: 12 }}>
            <label style={label}>Cave background</label>
            <AssetPicker
              value={doc.background}
              resolved={background}
              assets={assets}
              onPick={(id) => mutate({ ...doc, background: id || undefined })}
              onUpload={onUpload}
              onDeleteAsset={onDeleteAsset}
            />
            <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
              Optional backdrop behind the lobby, shown under the Cave overlays. Blank = the default treatment.
            </span>
          </div>
          {/* Cave accent (LSC-413): the per-Cave accent colour, rendered where the
              gold accent is (CTAs, offer, hairlines). A single controlled #rrggbb
              value; blank = the default gold. The native clients consume it via the
              lobby read's branding projection. */}
          <div style={{ marginTop: 12 }}>
            <label style={label}>Cave accent</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="color"
                aria-label="Cave accent colour"
                value={doc.accent || "#c9a227"}
                onChange={(e) => mutate({ ...doc, accent: e.target.value })}
                style={{ width: 44, height: 32, padding: 0, border: "none", background: "none", cursor: "pointer" }}
              />
              <code style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>{doc.accent || "default gold"}</code>
              {doc.accent && (
                <Button size="sm" variant="muted" aria-label="Reset accent to default" onClick={() => mutate({ ...doc, accent: undefined })}>
                  Reset
                </Button>
              )}
            </div>
            <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
              Optional accent colour, used where the Cave gold is (CTAs, offer, hairlines). Blank = the default gold.
            </span>
          </div>
        </Panel>

        {/* MODULES + INSPECTOR — a two-column authoring layout (LSC-258): the drag/
            resize canvas on the left, the selected module's field editor as a sticky
            inspector on the right, so edit controls stay in context with the clicked
            module (stacks on a narrow screen). */}
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "2 1 420px", minWidth: 0 }}>
            <Panel tier="quiet" title={`Modules (${doc.modules.length}/12)`}>
          {doc.modules.length < 12 && (
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Add module</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(Object.keys(KIND_LABEL) as LobbyModuleKind[]).map((k) => (
                  <Button key={k} size="sm" variant="muted" onClick={() => addModule(k)} aria-label={`Add ${KIND_LABEL[k]} module`}>
                    + {KIND_LABEL[k]}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {doc.modules.length === 0 ? (
            <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-dim)" }}>No modules yet — add one above.</p>
          ) : (
            <>
              <p style={{ margin: "0 0 8px", fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
                Drag ⠿ to reorder · drag the right edge to resize · click a card to edit it in the inspector.
              </p>
              <div className="lobby-module-canvas">
                <Grid
                  cols={6}
                  rowHeight={22}
                  margin={[8, 8]}
                  layout={deriveLobbyLayout(doc.modules) as unknown as Layout}
                  isDraggable
                  isResizable
                  draggableHandle=".lobby-drag"
                  resizeHandles={["e"]}
                  compactType="horizontal"
                  onLayoutChange={(l: Layout) => {
                    const next = applyLobbyLayout(l as unknown as LobbyLayoutItem[], doc.modules);
                    if (!sameOrderAndSpans(next, doc.modules)) mutate({ ...doc, modules: next });
                  }}
                >
                  {doc.modules.map((m, i) => (
                    <div key={m.id}>
                      <ModuleCard
                        module={m}
                        index={i}
                        total={doc.modules.length}
                        selected={m.id === selectedId}
                        onSelect={() => setSelectedId(m.id)}
                        onMove={(dir) => moveModule(m.id, dir)}
                        onRemove={() => removeModule(m.id)}
                        onToggleHidden={() => updateModule(m.id, { hidden: !m.hidden })}
                      />
                    </div>
                  ))}
                </Grid>
              </div>
            </>
          )}
            </Panel>
          </div>

          {/* SELECTED MODULE — the field editor for the clicked card, kept in context
              as a sticky inspector beside the canvas (LSC-258). */}
          <div style={{ flex: "1 1 320px", minWidth: 0, position: "sticky", top: 12 }}>
            {(() => {
              const sel = doc.modules.find((m) => m.id === selectedId) ?? doc.modules[0];
              if (!sel) return null;
              return (
                <Panel tier="quiet" title={`Edit · ${KIND_LABEL[sel.kind]}`}>
                  <ModuleDetail
                    module={sel}
                    assets={assets}
                    onUpdate={(patch) => updateModule(sel.id, patch)}
                    onContent={(patch) => updateContent(sel.id, patch)}
                    onUpload={onUpload}
                    onDeleteAsset={onDeleteAsset}
                  />
                </Panel>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
    </PendingUploadsCtx.Provider>
  );
}

// ── module canvas card (drag/resize) + detail editor ─────────────────────────

interface ModuleCardProps {
  module: LobbyModule;
  index: number;
  total: number;
  selected: boolean;
  onSelect: () => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  onToggleHidden: () => void;
}

// A compact card on the drag/resize canvas: the .lobby-drag header reorders (drag)
// and the RGL east handle resizes width; clicking selects it for the detail editor.
// Keyboard Move up/down + Hide/Remove stay on the card for a11y (drag is mouse-only).
function ModuleCard({ module: m, index, total, selected, onSelect, onMove, onRemove, onToggleHidden }: ModuleCardProps) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <div
      onClick={onSelect}
      data-lobby-module={m.id}
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${selected ? "var(--gold)" : "var(--border-faint)"}`,
        borderRadius: "var(--radius-sm)",
        background: m.hidden ? "var(--surface-quiet)" : "var(--surface-base)",
        opacity: m.hidden ? 0.7 : 1,
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <div
        className="lobby-drag"
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", cursor: "move", background: "var(--surface-quiet)", borderBottom: "1px solid var(--border-faint)" }}
      >
        <span aria-hidden style={{ color: "var(--text-dim)" }}>⠿</span>
        <span style={{ ...monoLabelStyle, color: "var(--gold)", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {KIND_LABEL[m.kind]}
        </span>
        {m.hidden && <Chip>Hidden</Chip>}
      </div>
      <div style={{ padding: 8, flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {m.title || KIND_LABEL[m.kind]}
        </div>
        <div style={{ ...monoLabelStyle, color: "var(--text-dim)", marginTop: 2 }}>{SPAN_LABEL[m.span]}</div>
      </div>
      <div style={{ display: "flex", gap: 3, padding: "0 6px 6px", flexWrap: "wrap" }} onClick={stop}>
        <Button size="sm" variant="muted" aria-label={`Move ${KIND_LABEL[m.kind]} up`} disabled={index === 0} onClick={() => onMove(-1)}>
          ↑
        </Button>
        <Button size="sm" variant="muted" aria-label={`Move ${KIND_LABEL[m.kind]} down`} disabled={index === total - 1} onClick={() => onMove(1)}>
          ↓
        </Button>
        <Button size="sm" variant="muted" aria-label={m.hidden ? `Show ${KIND_LABEL[m.kind]}` : `Hide ${KIND_LABEL[m.kind]}`} onClick={onToggleHidden}>
          {m.hidden ? "Show" : "Hide"}
        </Button>
        <Button size="sm" variant="muted" aria-label={`Remove ${KIND_LABEL[m.kind]}`} onClick={onRemove}>
          ✕
        </Button>
      </div>
    </div>
  );
}

interface ModuleDetailProps {
  module: LobbyModule;
  assets: LobbyAssetRow[];
  onUpdate: (patch: Partial<LobbyModule>) => void;
  onContent: (patch: Record<string, unknown>) => void;
  onUpload: (file: File, alt: string) => Promise<LobbyAssetRow | null>;
  onDeleteAsset: (id: string) => void;
}

function ModuleDetail({ module: m, assets, onUpdate, onContent, onUpload, onDeleteAsset }: ModuleDetailProps) {
  const spans = ALLOWED_SPANS[m.kind];
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={label}>Title</label>
          <input style={input} value={m.title} aria-label={`${KIND_LABEL[m.kind]} title`} onChange={(e) => onUpdate({ title: e.target.value })} />
        </div>
        <div style={{ width: 130 }}>
          <label style={label}>Width</label>
          <select style={input} aria-label={`${KIND_LABEL[m.kind]} width`} value={m.span} onChange={(e) => onUpdate({ span: Number(e.target.value) as LobbySpan })}>
            {spans.map((s) => (
              <option key={s} value={s}>
                {SPAN_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Editable per-Cave section eyebrow (LSC-258): blank = the LSC default shown
          as the placeholder, so a Cave can use its own voice without losing the default. */}
      <div style={{ marginBottom: 8 }}>
        <label style={label}>Section label</label>
        <input
          style={input}
          value={m.label ?? ""}
          aria-label={`${KIND_LABEL[m.kind]} section label`}
          placeholder={KIND_EYEBROW[m.kind]}
          maxLength={40}
          onChange={(e) => onUpdate({ label: e.target.value })}
        />
        <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
          The small heading above the module. Blank = “{KIND_EYEBROW[m.kind]}”.
        </span>
      </div>
      <ModuleContentEditor module={m} assets={assets} onUpdate={onUpdate} onContent={onContent} onUpload={onUpload} onDeleteAsset={onDeleteAsset} />
    </div>
  );
}

function ModuleContentEditor({ module: m, assets, onContent, onUpload, onDeleteAsset }: ModuleDetailProps) {
  const c = (m.content ?? {}) as Record<string, unknown>;
  const arr = <T,>(key: string): T[] => (Array.isArray(c[key]) ? (c[key] as T[]) : []);

  switch (m.kind) {
    case "story":
      return (
        <div style={{ display: "grid", gap: 8 }}>
          <div>
            <label style={label}>Body</label>
            <textarea
              style={{ ...input, minHeight: 90, resize: "vertical" }}
              aria-label="Story body"
              value={(c.body as string) ?? ""}
              onChange={(e) => onContent({ body: e.target.value })}
            />
          </div>
          <TextField label="Caption" ariaLabel="Story caption" value={(c.caption as string) ?? ""} onChange={(v) => onContent({ caption: v })} />
          <TextField label="Link URL (https)" ariaLabel="Story link URL" value={(c.linkUrl as string) ?? ""} onChange={(v) => onContent({ linkUrl: v })} />
          <TextField label="Link label" ariaLabel="Story link label" value={(c.linkLabel as string) ?? ""} onChange={(v) => onContent({ linkLabel: v })} />
          <div>
            <label style={label}>Image</label>
            <AssetPicker
              value={(c.imageAssetId as string) ?? undefined}
              resolved={c.imageAssetId ? assets.find((a) => a.id === c.imageAssetId) : undefined}
              assets={assets}
              onPick={(id) => onContent({ imageAssetId: id, imageAlt: id ? assets.find((a) => a.id === id)?.alt : undefined })}
              onUpload={onUpload}
              onDeleteAsset={onDeleteAsset}
            />
          </div>
        </div>
      );
    case "stats":
      return (
        <ListEditor
          items={arr<{ val: string; label: string }>("items")}
          emptyItem={{ val: "", label: "" }}
          min={2}
          max={6}
          onChange={(items) => onContent({ items })}
          render={(it, set) => (
            <>
              <input style={{ ...input, flex: 1 }} aria-label="Stat value" placeholder="Value" value={it.val} onChange={(e) => set({ ...it, val: e.target.value })} />
              <input style={{ ...input, flex: 2 }} aria-label="Stat label" placeholder="Label" value={it.label} onChange={(e) => set({ ...it, label: e.target.value })} />
            </>
          )}
        />
      );
    case "programming":
      return (
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Mode</label>
            <select style={input} aria-label="Programming mode" value={(c.mode as string) ?? "both"} onChange={(e) => onContent({ mode: e.target.value })}>
              <option value="upcoming">Upcoming</option>
              <option value="replays">Replays</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div style={{ width: 130 }}>
            <label style={label}>Preview count</label>
            <select style={input} aria-label="Programming preview count" value={(c.previewCount as number) ?? 4} onChange={(e) => onContent({ previewCount: Number(e.target.value) })}>
              {[3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    case "linkedCards":
      return (
        <ListEditor
          items={arr<{ title: string; desc?: string; actionLabel?: string; actionUrl?: string }>("cards")}
          emptyItem={{ title: "" }}
          min={1}
          max={6}
          onChange={(cards) => onContent({ cards })}
          render={(it, set) => (
            <div style={{ display: "grid", gap: 4, flex: 1 }}>
              <input style={input} aria-label="Card title" placeholder="Title" value={it.title} onChange={(e) => set({ ...it, title: e.target.value })} />
              <input style={input} aria-label="Card description" placeholder="Description" value={it.desc ?? ""} onChange={(e) => set({ ...it, desc: e.target.value })} />
              <input style={input} aria-label="Card action URL (https)" placeholder="Action URL (https)" value={it.actionUrl ?? ""} onChange={(e) => set({ ...it, actionUrl: e.target.value })} />
            </div>
          )}
        />
      );
    case "partners":
      return (
        <ListEditor
          items={arr<{ name: string; tier?: string; url?: string }>("entries")}
          emptyItem={{ name: "" }}
          min={1}
          max={8}
          onChange={(entries) => onContent({ entries })}
          render={(it, set) => (
            <div style={{ display: "grid", gap: 4, flex: 1 }}>
              <input style={input} aria-label="Partner name" placeholder="Name" value={it.name} onChange={(e) => set({ ...it, name: e.target.value })} />
              <input style={input} aria-label="Partner tier" placeholder="Tier (optional)" value={it.tier ?? ""} onChange={(e) => set({ ...it, tier: e.target.value })} />
              <input style={input} aria-label="Partner URL (https)" placeholder="URL (https, optional)" value={it.url ?? ""} onChange={(e) => set({ ...it, url: e.target.value })} />
            </div>
          )}
        />
      );
    case "social":
      return (
        <ListEditor
          items={arr<{ platform: string; url: string }>("links")}
          emptyItem={{ platform: "", url: "" }}
          min={1}
          max={6}
          onChange={(links) => onContent({ links })}
          render={(it, set) => (
            <>
              <input style={{ ...input, flex: 1 }} aria-label="Social platform" placeholder="Platform" value={it.platform} onChange={(e) => set({ ...it, platform: e.target.value })} />
              <input style={{ ...input, flex: 2 }} aria-label="Social URL (https)" placeholder="URL (https)" value={it.url} onChange={(e) => set({ ...it, url: e.target.value })} />
            </>
          )}
        />
      );
  }
}

function TextField({ label: lbl, ariaLabel, value, onChange }: { label: string; ariaLabel: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label style={label}>{lbl}</label>
      <input style={input} aria-label={ariaLabel} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// A generic add/remove list editor for repeated module rows (stats, cards, …).
function ListEditor<T>({
  items,
  emptyItem,
  min,
  max,
  onChange,
  render,
}: {
  items: T[];
  emptyItem: T;
  min: number;
  max: number;
  onChange: (items: T[]) => void;
  render: (item: T, set: (next: T) => void) => React.ReactNode;
}) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          {render(it, (next) => onChange(items.map((x, j) => (j === i ? next : x))))}
          <Button
            size="sm"
            variant="muted"
            aria-label={`Remove row ${i + 1}`}
            disabled={items.length <= min}
            onClick={() => onChange(items.filter((_, j) => j !== i))}
          >
            ✕
          </Button>
        </div>
      ))}
      {items.length < max && (
        <Button size="sm" variant="muted" aria-label="Add row" onClick={() => onChange([...items, { ...emptyItem }])}>
          + Add
        </Button>
      )}
    </div>
  );
}

// ── asset picker (upload with required alt / pick existing / clear) ──────────

// altFromFilename derives a human alt string from an upload filename (LSC-385):
// drop the extension, turn separators into spaces, collapse whitespace.
function altFromFilename(name: string): string {
  return name
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function AssetPicker({
  value,
  resolved,
  assets,
  onPick,
  onUpload,
  onDeleteAsset,
}: {
  value?: string;
  resolved?: { url: string; alt: string };
  assets: LobbyAssetRow[];
  onPick: (id: string | undefined) => void;
  onUpload: (file: File, alt: string) => Promise<LobbyAssetRow | null>;
  onDeleteAsset: (id: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Report a selected-but-not-uploaded file to the editor so Publish is blocked
  // until it's uploaded or cleared (LSC-337).
  const setPending = useContext(PendingUploadsCtx);
  const pickerId = useId();
  useEffect(() => {
    setPending?.(pickerId, !!file);
  }, [file, pickerId, setPending]);
  useEffect(() => () => setPending?.(pickerId, false), [pickerId, setPending]);

  const doUpload = async () => {
    if (!file || !alt.trim()) return;
    setUploading(true);
    const a = await onUpload(file, alt.trim());
    setUploading(false);
    if (a) {
      onPick(a.id);
      setFile(null);
      setAlt("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div style={{ display: "grid", gap: 6, border: "1px solid var(--border-faint)", borderRadius: "var(--radius-sm)", padding: 8 }}>
      {resolved ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src={resolved.url} alt={resolved.alt} style={{ width: 48, height: 48, objectFit: "cover", borderRadius: "var(--radius-sm)" }} />
          <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{resolved.alt}</span>
          <Button size="sm" variant="muted" aria-label="Clear image" onClick={() => onPick(undefined)}>
            Clear
          </Button>
        </div>
      ) : (
        <span style={{ ...monoLabelStyle, color: "var(--text-dim)" }}>No image</span>
      )}
      {assets.length > 0 && (
        <select
          style={input}
          aria-label="Choose an uploaded image"
          value={value ?? ""}
          onChange={(e) => onPick(e.target.value || undefined)}
        >
          <option value="">— Choose uploaded image —</option>
          {assets.map((a) => (
            <option key={a.id} value={a.id}>
              {a.alt || a.id}
            </option>
          ))}
        </select>
      )}
      <div style={{ display: "grid", gap: 4 }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif"
          aria-label="Upload image file"
          style={{ fontSize: "var(--fs-2xs)" }}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);
            // Prefill alt from the filename (LSC-385) so the required field starts
            // populated; the operator can still edit it. Only when alt is empty.
            if (f && !alt.trim()) setAlt(altFromFilename(f.name));
          }}
        />
        {file && (
          <>
            <input style={input} aria-label="Image alt text" placeholder="Alt text (required)" value={alt} onChange={(e) => setAlt(e.target.value)} />
            <Button size="sm" variant="accent" aria-label="Upload image" disabled={uploading || !alt.trim()} onClick={doUpload}>
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </>
        )}
      </div>
      {value && resolved && (
        <Button
          size="sm"
          variant="muted"
          aria-label="Delete uploaded image"
          onClick={() => onDeleteAsset(value)}
          style={{ justifySelf: "start" }}
        >
          Delete from library
        </Button>
      )}
    </div>
  );
}
